import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface FluxoDiarioLinha {
  dataIso: string;
  dataExibicao: string;
  diaSemana: string;
  saldoInicial: number;
  receberDia: number;
  pagarDia: number;
  saldoFinal: number;
  contasReceber: number;
  chequePreReceber: number;
  contasPagar: number;
  chequePrePagar: number;
}

@Component({
  selector: 'app-fluxo-caixa-diario-projetado',
  templateUrl: './fluxo-caixa-diario-projetado.component.html',
  styleUrls: ['./fluxo-caixa-diario-projetado.component.scss']
})
export class FluxoCaixaDiarioProjetadoComponent implements OnInit {
  ano = new Date().getFullYear();
  saldoInicialPeriodo = 0;
  filtroPrevisaoReal: 'T' | 'R' | 'P' = 'T';
  linhas: FluxoDiarioLinha[] = [];

  saldoDiaData = '';
  saldoDiaValor = 0;
  saldoFinalData = '';
  saldoFinalValor = 0;

  loading = false;
  arrastandoTabela = false;
  private posicaoInicialX = 0;
  private scrollInicialX = 0;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarRelatorio();
  }

  alterarAno(delta: number): void {
    this.ano += delta;
    this.carregarRelatorio();
  }

  carregarRelatorio(): void {
    const inicioAno = `${this.ano}-01-01`;
    const fimAno = `${this.ano}-12-31`;
    const urlReceberAberto = `${API}financeiro/FinContaReceber/ConsultarFinContaReceber?dataVencimentoInicio=${inicioAno}&dataVencimentoFim=${fimAno}&pago=false`;
    const urlReceberPago = `${API}financeiro/FinContaReceber/ConsultarFinContaReceber?dataVencimentoInicio=${inicioAno}&dataVencimentoFim=${fimAno}&pago=true`;
    const urlPagar = `${API}financeiro/FinContaPagar/ConsultarFinContaPagar`;

    this.loading = true;
    this.util.showLoading();

    forkJoin({
      receberAberto: this.ajaxService.get(urlReceberAberto),
      receberPago: this.ajaxService.get(urlReceberPago),
      pagar: this.ajaxService.get(urlPagar)
    }).subscribe({
      next: ({ receberAberto, receberPago, pagar }) => {
        this.loading = false;
        this.util.closeLoading();

        const receberTodos = this.unirPorId(
          this.extrairListaResponse(receberAberto),
          this.extrairListaResponse(receberPago)
        ).map((x: any) => this.util.converterParaLowercase(x || {}));

        const pagarTodos = this.extrairListaResponse(pagar)
          .map((x: any) => this.util.converterParaLowercase(x || {}));

        this.montarLinhas(
          receberTodos.filter((x: any) => this.entraNoFluxo(x)),
          pagarTodos.filter((x: any) => this.entraNoFluxo(x))
        );
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.linhas = [];
        this.util.toasterShowError('Erro', 'Nao foi possivel carregar o fluxo diario projetado.');
      }
    });
  }

  formatMoney(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  selecionarPrevisaoReal(tipo: 'T' | 'R' | 'P'): void {
    this.filtroPrevisaoReal = tipo;
    this.carregarRelatorio();
  }

  private montarLinhas(receberLista: any[], pagarLista: any[]): void {
    const mapaReceber = new Map<string, number>();
    const mapaPagar = new Map<string, number>();

    receberLista.forEach((item: any) => {
      const data = this.normalizarDataIso(item.datavencimento);
      if (!data || !data.startsWith(`${this.ano}-`)) return;
      const valor = Number(item.valordocumento || 0);
      mapaReceber.set(data, Number(mapaReceber.get(data) || 0) + valor);
    });

    pagarLista.forEach((item: any) => {
      const data = this.normalizarDataIso(item.datavencimento);
      if (!data || !data.startsWith(`${this.ano}-`)) return;
      const valor = Number(item.valordocumento || 0);
      mapaPagar.set(data, Number(mapaPagar.get(data) || 0) + valor);
    });

    const linhas: FluxoDiarioLinha[] = [];
    let saldo = Number(this.saldoInicialPeriodo || 0);
    const data = new Date(this.ano, 0, 1);
    const fim = new Date(this.ano, 11, 31);

    while (data <= fim) {
      const iso = this.dateToIso(data);
      const receberDia = Number(mapaReceber.get(iso) || 0);
      const pagarDia = Number(mapaPagar.get(iso) || 0);
      const saldoInicial = saldo;
      const saldoFinal = saldoInicial + receberDia - pagarDia;
      saldo = saldoFinal;

      linhas.push({
        dataIso: iso,
        dataExibicao: this.dateToBr(data),
        diaSemana: this.diaSemanaPt(data),
        saldoInicial,
        receberDia,
        pagarDia,
        saldoFinal,
        contasReceber: receberDia,
        chequePreReceber: 0,
        contasPagar: pagarDia,
        chequePrePagar: 0
      });

      data.setDate(data.getDate() + 1);
    }

    this.linhas = linhas;
    const hojeIso = this.dateToIso(new Date());
    const linhaSaldoDia = linhas.find((x) => x.dataIso === hojeIso) || linhas[0];
    this.saldoDiaData = linhaSaldoDia?.dataExibicao || '';
    this.saldoDiaValor = Number(linhaSaldoDia?.saldoFinal || 0);
    this.saldoFinalData = linhas.length ? linhas[linhas.length - 1].dataExibicao : '';
    this.saldoFinalValor = linhas.length ? Number(linhas[linhas.length - 1].saldoFinal || 0) : 0;
  }

  private normalizarDataIso(valor: any): string {
    if (!valor) return '';
    if (valor instanceof Date && !Number.isNaN(valor.getTime())) return this.dateToIso(valor);
    const texto = String(valor).trim();
    const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) return '';
    return this.dateToIso(data);
  }

  private dateToIso(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private dateToBr(data: Date): string {
    return data.toLocaleDateString('pt-BR');
  }

  private diaSemanaPt(data: Date): string {
    return data.toLocaleDateString('pt-BR', { weekday: 'long' });
  }

  private correspondePrevisaoReal(valor: any): boolean {
    if (this.filtroPrevisaoReal === 'T') return true;
    const normalizado = String(valor || 'R').trim().toUpperCase();
    return normalizado === this.filtroPrevisaoReal;
  }

  private entraNoFluxo(item: any): boolean {
    if (!this.correspondePrevisaoReal(item?.previsaoreal)) return false;
    const tipo = String(item?.previsaoreal || 'R').trim().toUpperCase();
    if (tipo === 'P') return !this.estaPago(item);
    return true;
  }

  private estaPago(item: any): boolean {
    const valor = item?.pago;
    if (valor === true || valor === 1) return true;
    const texto = String(valor ?? '').trim().toLowerCase();
    return texto === 'true' || texto === '1';
  }

  private unirPorId(aberto: any[], pago: any[]): any[] {
    const mapa = new Map<string, any>();
    [...aberto, ...pago].forEach((item: any) => {
      const id = item?.id ?? item?.ID;
      mapa.set(id != null ? String(id) : `sem-id-${mapa.size}`, item);
    });
    return Array.from(mapa.values());
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) return atual;
    if (atual && typeof atual === 'object') return Object.values(atual).filter((x: any) => x && typeof x === 'object');
    return [];
  }
}
